/**
 * @file Renders the table of an organization's public keys, with the actions to add, view and revoke one.
 */

import React, { useRef, useState, useCallback } from "react";
import { Alert, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Table, { ASCEND } from "@components/Table";
import { W1 } from "@components/Constants";
import { hasAccess, isConsoleUser } from "@components/Authorization";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { indexOrganizationPublicKeysOperation as indexOperation } from "@api/services/identity";
import { type OrganizationPublicKey } from "@api/types";

import useColumnsConfig from "./helpers/useColumnsConfig";
import OrganizationPublicKeyGuideModal from "./OrganizationPublicKeyGuideModal";
import OrganizationPublicKeyDetailsModal from "./OrganizationPublicKeyDetailsModal";
import OrganizationPublicKeyCreateModal, {
  type OrganizationPublicKeyCreateModalHandle,
} from "./OrganizationPublicKeyCreateModal";

const LABEL_TITLE = "Public Keys";
const LABEL_CREATE = "Add Public Key";
const LABEL_GUIDE = "See how to create a key pair";
const DESCRIPTION = "Public keys let your client call the API without a browser. Your client signs an ID token with its private key, and the platform verifies that token with the public key you add here. Each key serves one API: the Investor API signs in your own investors, and the Back Office API acts as the user that adds the key.";
const LABEL_SECOND_FACTOR_OFF = "Two factor authentication is off";
const DESCRIPTION_SECOND_FACTOR_OFF = "Enable two factor authentication in your profile to add a public key. A public key authenticates a client with no second factor and with no expiration, therefore the account of the person that adds it must have the second factor.";

const PERMISSION_WRITE = "organization-public-keys-write";

type TableHandle = {
  addItem: (item: OrganizationPublicKey) => void;
  updateItem: (
    id: string,
    attributes: Partial<OrganizationPublicKey>,
  ) => void;
};

/**
 * Renders the public keys table together with its create, details and guide modals.
 */
const OrganizationPublicKeysTable = (): JSX.Element => {
  const { isRoot, identity } = useAppContext() as AppContext;

  const table = useRef<TableHandle>({} as TableHandle);
  const createModal = useRef<
    OrganizationPublicKeyCreateModalHandle | Record<string, never>
  >({});

  const [ selectedPublicKey, setSelectedPublicKey ] =
    useState<OrganizationPublicKey>();

  const [ isGuideOpen, setIsGuideOpen ] = useState(false);

  // NOTE: the details modal opens on the new key, because the client needs
  //       each claim of the ID token and the example, and not the two values
  //       alone.
  /**
   * Adds the new key to the table and opens its details modal.
   */
  const onCreated = (newPublicKey: OrganizationPublicKey) => {
    table.current.addItem(newPublicKey);
    setSelectedPublicKey(newPublicKey);
  };

  // NOTE: the columns config keeps its identity while these handlers keep
  //       theirs, and a new config makes the table load the items again.
  const onRevoked = useCallback((revokedPublicKey: OrganizationPublicKey) =>
    table.current.updateItem(revokedPublicKey.id, revokedPublicKey)
  , []);

  const onSelected = useCallback((newSelectedPublicKey: OrganizationPublicKey) =>
    setSelectedPublicKey(newSelectedPublicKey)
  , []);

  /**
   * Closes the details modal by clearing the selected key.
   */
  const closeDetailsModal = () => setSelectedPublicKey(undefined);

  /**
   * Opens the key pair guide modal.
   */
  const openGuideModal = () => setIsGuideOpen(true);

  /**
   * Closes the key pair guide modal.
   */
  const closeGuideModal = () => setIsGuideOpen(false);

  /**
   * Opens the create modal, unless the second factor requirement blocks it.
   */
  const openCreateModal = () => {
    if (shouldBlockCreate) {
      return;
    }

    createModal.current.open?.();
  };

  const columnsConfig = useColumnsConfig({ onSelected, onRevoked });

  const hasWriteAccess = hasAccess([ PERMISSION_WRITE ]);

  // NOTE: a public key belongs to the organization and to the person that
  //       adds it. A console user and a root user act on each organization
  //       and hold no user of this one, therefore they add no key here.
  const isPlatformUser = isRoot === true || isConsoleUser();

  const canCreate = hasWriteAccess && !isPlatformUser;

  // NOTE: a public key is a long lived credential with no second factor of
  //       its own, therefore the account of the person that adds it must
  //       hold the second factor. CreateOrganizationPublicKey makes the same
  //       examination, and it refuses the operation.
  const hasSecondFactor = identity?.is2FaEnabled === true;

  const shouldBlockCreate = canCreate && !hasSecondFactor;

  const createAction = {
    icon: <PlusOutlined />,
    action: openCreateModal,
    title: LABEL_CREATE,
    disabled: shouldBlockCreate,
  };

  const headerActions = canCreate
    ? [ createAction ]
    : [];

  const alertStyle = {
    marginBottom: W1,
  };

  const renderedDescription = shouldBlockCreate
    ? (
      <Alert
        showIcon
        type="warning"
        style={alertStyle}
        message={LABEL_SECOND_FACTOR_OFF}
        description={DESCRIPTION_SECOND_FACTOR_OFF}
      />
    )
    : (
      <Typography.Paragraph type="secondary">
        {DESCRIPTION}

        {canCreate && (
          <>
            {" "}

            <Typography.Link onClick={openGuideModal}>
              {LABEL_GUIDE}
            </Typography.Link>
          </>
        )}
      </Typography.Paragraph>
    );

  const tableProps = {
    size: "small" as const,
    table,
    className: "last-column-action",
    headerActions,
    columnsConfig,
    indexOperation,
    defaultSortColumn: "name",
    defaultSortColumnDirection: ASCEND,
  };

  return (
    <>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {LABEL_TITLE}
      </Typography.Title>

      {renderedDescription}

      <Table {...tableProps} />

      {canCreate && (
        <OrganizationPublicKeyCreateModal
          modal={createModal}
          onSuccess={onCreated}
        />
      )}

      <OrganizationPublicKeyDetailsModal
        isOpen={!!selectedPublicKey}
        publicKey={selectedPublicKey}
        onClose={closeDetailsModal}
      />

      <OrganizationPublicKeyGuideModal
        isOpen={isGuideOpen}
        onClose={closeGuideModal}
      />
    </>
  );
};

export default OrganizationPublicKeysTable;
